import type { Letter, OrgSimpleListing, TransactionStatus, UUID } from '@endaoment-frontend/types';
import { uuidSchema } from '@endaoment-frontend/types';

export const generateUUID = (): UUID => uuidSchema.parse(crypto.randomUUID());

export const getNTEEMajorCode = (org?: Pick<OrgSimpleListing, 'nteeCode'>): Letter => {
  if (!org || !org.nteeCode) return 'Z';
  try {
    return org.nteeCode.trim()[0].toUpperCase() as Letter;
  } catch {
    return 'Z';
  }
};

export const stringToColor = (stringInput = ''): string => {
  const stringUniqueHash = stringInput.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  return `hsl(${stringUniqueHash % 360}, 95%, 35%)`;
};

export const arraySliceNextN = <T>(orig: Array<T>, index: number, length: number): Array<T> => {
  const arr: Array<T> = [];
  let curr = index + 1;
  for (let i = 0; i < length; i++) {
    if (curr > orig.length - 1) {
      if (length > orig.length) break;
      curr = 0;
    }
    arr.push(orig[curr]);
    curr++;
  }
  return arr;
};

export const isViewableBlockchainStatus = (status: TransactionStatus): status is 'error' | 'pending' | 'success' =>
  status === 'error' || status === 'pending' || status === 'success';

export const chunkArray = <T>(input: Array<T>, size: number): Array<Array<T>> =>
  input.reduce(
    (arr: Array<Array<T>>, item, idx) =>
      idx % size === 0 ? [...arr, [item]] : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]],
    [],
  );

export const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export const arrToSorted = <T>(arr: Array<T>, compareFn?: (a: T, b: T) => number): Array<T> => {
  if (arr.length === 0) return arr;

  const copy = [...arr];
  copy.sort(compareFn);
  return copy;
};

/**
 * Calculate the total USDC amount needed to offset fees so the recipient receives the full intended amount.
 *
 * @param amount - The base USDC amount (as bigint with 6 decimals)
 * @param feeBps - The fee in basis points (0-10000, where 10000 = 100%)
 * @returns The total USDC amount needed including the offset to cover fees
 * @throws Error if feeBps is not between 0 and 10000
 *
 * @example
 * // For $5.00 with 1% fee (100 bps):
 * // Input: 5_000_000n (USDC), 100 (bps)
 * // Output: 5_050_505n (≈$5.05 to ensure recipient gets $5.00 after 1% fee)
 */
export const calculateOffsetFeeAmount = (amount: bigint, feeBps: number): bigint => {
  if (feeBps < 0 || feeBps >= 10_000) throw new Error(`feeBps must be in [0, 10_000), got ${feeBps}`);

  return (amount * 10_000n) / (10_000n - BigInt(feeBps));
};
